import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import api from '../../services/api';
import { fetchCEP } from '../../services/viacep';
import { maskCEP } from '../../hooks/useMask';

import StepBar from '../../components/StepBar/StepBar';
import Field from '../../components/Field/Field';
import Input from '../../components/Input/Input';
import { PrimaryButton } from '../../components/Button/Button';
import Alert from '../../components/Alert/Alert';
import LoadingModal from '../../components/LoadingModal/LoadingModal';

import styles from './Endereco.module.css';

const EMPTY_ERRORS = {
  logradouro: '', numero: '', bairro: '', uf: '', cidade: '', email: '',
};

const CREATE_STATUS = {
  PENDING: 'pending',
  DONE:    'done',
  ERROR:   'error',
};

export default function Endereco() {
  const navigate = useNavigate();

  const prefill = (() => {
    try { return JSON.parse(sessionStorage.getItem('address_prefill') || '{}'); }
    catch { return {}; }
  })();

  const [form, setForm] = useState({
    logradouro:  prefill.logradouro || '',
    numero:      '',
    complemento: '',
    bairro:      prefill.bairro || '',
    uf:          prefill.uf     || '',
    cidade:      prefill.cidade || '',
    email:       '',
  });

  const [cep, setCep] = useState(
      sessionStorage.getItem('cep_raw')
          ? sessionStorage.getItem('cep_raw').replace(/(\d{5})(\d)/, '$1-$2')
          : ''
  );

  const [optin,        setOptin]        = useState(true);
  const [errors,       setErrors]       = useState(EMPTY_ERRORS);
  const [alert,        setAlert]        = useState('');
  const [submitting,   setSubmitting]   = useState(false);
  const [checking,     setChecking]     = useState(false);
  const [cepBusy,      setCepBusy]      = useState(false);
  const [createStatus, setCreateStatus] = useState(
      sessionStorage.getItem('encode_data') ? CREATE_STATUS.DONE : CREATE_STATUS.PENDING
  );

  useEffect(() => {
    if (sessionStorage.getItem('encode_data')) {
      setCreateStatus(CREATE_STATUS.DONE);
      return;
    }

    const promise = window.__createPromise;
    if (!promise) return;

    promise
        .then(() => setCreateStatus(CREATE_STATUS.DONE))
        .catch(() => setCreateStatus(CREATE_STATUS.ERROR));
  }, []);

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  }

  async function handleCepBlur() {
    const raw = cep.replace(/\D/g, '');
    if (raw.length !== 8) return;
    setCepBusy(true);
    try {
      const data = await fetchCEP(raw);
      setForm(prev => ({
        ...prev,
        logradouro: data.logradouro || prev.logradouro,
        bairro:     data.bairro     || prev.bairro,
        cidade:     data.cidade     || prev.cidade,
        uf:         data.uf         || prev.uf,
      }));
      sessionStorage.setItem('cep_raw', raw);
    } catch {/* mantém o que tinha */}
    finally { setCepBusy(false); }
  }

  function validate() {
    const e = { ...EMPTY_ERRORS };
    let ok = true;

    if (!form.logradouro.trim()) { e.logradouro = 'Campo obrigatório'; ok = false; }
    if (!form.numero.trim())     { e.numero     = 'Campo obrigatório'; ok = false; }
    if (!form.bairro.trim())     { e.bairro     = 'Campo obrigatório'; ok = false; }
    if (!form.uf.trim())         { e.uf         = 'Campo obrigatório'; ok = false; }
    if (!form.cidade.trim())     { e.cidade     = 'Campo obrigatório'; ok = false; }

    if (!form.email.trim()) {
      e.email = 'Insira seu e-mail'; ok = false;
    } else if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) {
      e.email = 'E-mail inválido'; ok = false;
    }

    if (!optin) {
      setAlert('Você precisa aceitar os termos para continuar.');
      return false;
    }

    setErrors(e);
    return ok;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setAlert('');
    if (!validate()) return;

    setSubmitting(true);

    try {
      if (createStatus === CREATE_STATUS.PENDING && window.__createPromise) {
        await window.__createPromise;
      }

      if (createStatus === CREATE_STATUS.ERROR || window.__createError) {
        setAlert(window.__createError || 'Erro ao processar seus dados. Volte e tente novamente.');
        setSubmitting(false);
        return;
      }

      const encode_data = sessionStorage.getItem('encode_data');
      if (!encode_data) {
        setAlert('Sessão expirada. Volte e preencha seus dados novamente.');
        setSubmitting(false);
        return;
      }

      const cepRaw = cep.replace(/\D/g, '');

      const body = {
        endereco: {
          cep:         cepRaw.padStart(8, '0'),
          cidade:      form.cidade,
          uf:          form.uf.toUpperCase(),
          logradouro:  form.logradouro,
          numero:      form.numero,
          complemento: form.complemento || '',
          bairro:      form.bairro,
        },
        email: form.email,
      };

      const { data: updateData } = await api.put(`/leads/update/${encode_data}`, body);

      sessionStorage.setItem('sessionUf',     form.uf);
      sessionStorage.setItem('sessionCidade', form.cidade);
      sessionStorage.setItem('payload_email', form.email);
      sessionStorage.setItem('cod_erro', String(updateData?.cod_erro ?? ''));
      sessionStorage.setItem(
        'msg_erro',
        typeof updateData?.msg_erro === 'string'
          ? updateData.msg_erro
          : JSON.stringify(updateData?.msg_erro ?? '')
      );

      const createCodErro = parseInt(sessionStorage.getItem('create_cod_erro') ?? '0', 10);
      const target = createCodErro === 200 ? '/simulacao' : '/contato';

      // Delay proposital: simula a consulta de ofertas no banco antes de navegar
      setChecking(true);
      const fakeDelay = 3500 + Math.random() * 1500; // 3.5s – 5s
      await new Promise(resolve => setTimeout(resolve, fakeDelay));

      navigate(target);

    } catch {
      setAlert('Ocorreu um erro ao salvar. Tente novamente em instantes.');
    } finally {
      setSubmitting(false);
      setChecking(false);
    }
  }

  /* Texto do modal dependendo do momento */
  const modalText = checking
      ? 'Consultando as melhores condições para você...'
      : submitting
      ? createStatus === CREATE_STATUS.PENDING
          ? 'Aguardando validação...'
          : 'Salvando endereço...'
      : 'Validando seus dados...';

  const showModal = submitting;

  return (
      <div className={styles.wrap}>

        {showModal && <LoadingModal text={modalText} />}

        <StepBar current={2} />

        <h1 className={styles.title}>Informe seu endereço residencial</h1>
        <p className={styles.sub}>Precisamos para verificar sua elegibilidade na região.</p>

        <form onSubmit={handleSubmit} noValidate className={styles.form}>

          <Field label="CEP" id="cep_e">
            <Input
                id="cep_e"
                type="text"
                placeholder="00000-000"
                inputMode="numeric"
                value={cep}
                onChange={e => setCep(maskCEP(e.target.value))}
                onBlur={handleCepBlur}
                disabled={cepBusy}
            />
            {cepBusy && <p className={styles.cepMsg}>Buscando CEP...</p>}
          </Field>

          <Field label="Endereço" id="logradouro" error={errors.logradouro}>
            <Input
                id="logradouro"
                type="text"
                placeholder="Rua, Avenida..."
                hasError={!!errors.logradouro}
                value={form.logradouro}
                onChange={e => set('logradouro', e.target.value)}
            />
          </Field>

          <div className={styles.row2}>
            <Field label="Número" id="numero" error={errors.numero}>
              <Input
                  id="numero"
                  type="text"
                  placeholder="Nº"
                  inputMode="numeric"
                  hasError={!!errors.numero}
                  value={form.numero}
                  onChange={e => set('numero', e.target.value)}
              />
            </Field>
            <Field label="Complemento" id="complemento">
              <Input
                  id="complemento"
                  type="text"
                  placeholder="Apto, Casa..."
                  value={form.complemento}
                  onChange={e => set('complemento', e.target.value)}
              />
            </Field>
          </div>

          <Field label="Bairro" id="bairro" error={errors.bairro}>
            <Input
                id="bairro"
                type="text"
                placeholder="Bairro"
                hasError={!!errors.bairro}
                value={form.bairro}
                onChange={e => set('bairro', e.target.value)}
            />
          </Field>

          <div className={styles.rowUf}>
            <Field label="UF" id="uf" error={errors.uf}>
              <Input
                  id="uf"
                  type="text"
                  placeholder="SP"
                  maxLength={2}
                  hasError={!!errors.uf}
                  value={form.uf}
                  onChange={e => set('uf', e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
              />
            </Field>
            <Field label="Cidade" id="cidade" error={errors.cidade}>
              <Input
                  id="cidade"
                  type="text"
                  placeholder="Cidade"
                  hasError={!!errors.cidade}
                  value={form.cidade}
                  onChange={e => set('cidade', e.target.value)}
              />
            </Field>
          </div>

          <Field label="E-mail" id="email" error={errors.email}>
            <Input
                id="email"
                type="email"
                placeholder="email@dominio.com"
                hasError={!!errors.email}
                value={form.email}
                onChange={e => set('email', e.target.value)}
            />
          </Field>

          <div className={styles.optinRow}>
            <input
                type="checkbox"
                id="lgpd"
                className={styles.checkbox}
                checked={optin}
                onChange={e => { setOptin(e.target.checked); setAlert(''); }}
            />
            <label htmlFor="lgpd" className={styles.optinLabel}>
              Aceito receber informações pelos canais de relacionamento (SMS, telefone,
              WhatsApp e email) e estou de acordo com a{' '}
              <a href="#privacidade" className={styles.link}>Política de Privacidade</a> e os{' '}
              <a href="#termos" className={styles.link}>Termos de Uso</a>.
            </label>
          </div>

          {alert && <Alert>{alert}</Alert>}

          <PrimaryButton type="submit" loading={submitting}>
            Continuar
          </PrimaryButton>

        </form>
      </div>
  );
}